import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import Spinner from '@/components/Spinner.tsx';
import { showToast } from '@/components/Toast.tsx';
import useAxiosInterceptors from '@/hooks/useAxiosInterceptors.tsx';
import { useCompleteLogin } from '@/hooks/useCompleteLogin.tsx';
import { useEntityConfig } from '@/hooks/useEntityConfig.tsx';
import { useNavigateRedirect } from '@/hooks/useNavigateRedirect.tsx';
import { isAllowedOAuthReturnUrl, takeOAuthReturnTo } from '@/utils/embedAuth.ts';

const B2CoreOAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const config = useEntityConfig();
  const axiosPrivate = useAxiosInterceptors();
  const completeLogin = useCompleteLogin();
  const { handleNavigateRedirect } = useNavigateRedirect();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) {
      return;
    }

    handled.current = true;

    const returnTo = takeOAuthReturnTo();
    const redirectToEmbedder = (): boolean => {
      if (
        !returnTo ||
        !isAllowedOAuthReturnUrl(returnTo, [config.VITE_TRADING_VIEW_URL, config.VITE_TRADING_VIEW_REPLAY_URL])
      ) {
        return false;
      }

      window.location.assign(returnTo);

      return true;
    };

    const oauthError = searchParams.get('error');

    if (oauthError) {
      showToast({
        type: 'error',
        title: t('error_title'),
        description: t(`oauth_error_${oauthError}`, {
          defaultValue: t('oauth_login_failed'),
        }),
      });

      if (redirectToEmbedder()) {
        return;
      }

      navigate('/login', { replace: true });

      return;
    }

    const completeOAuth = async () => {
      try {
        const response = await axiosPrivate.put<{ access_token: string | null }>('/auth/refresh-token');
        const accessToken = response.data.access_token;

        if (!accessToken) {
          throw new Error('missing access token');
        }

        if (redirectToEmbedder()) {
          return;
        }

        if (handleNavigateRedirect()) {
          return;
        }

        await completeLogin(accessToken);

        navigate('/', { replace: true });
      } catch {
        showToast({
          type: 'error',
          title: t('error_title'),
          description: t('oauth_login_failed'),
        });

        if (redirectToEmbedder()) {
          return;
        }

        navigate('/login', { replace: true });
      }
    };

    void completeOAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Spinner />;
};

export default B2CoreOAuthCallback;
